import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowRight, Landmark, ChevronRight, ExternalLink } from 'lucide-react';
import Seo from '../components/Seo';
import LandingHeader from '../components/LandingHeader';
import RelatedLinks from '../components/RelatedLinks';

const JURISDICTIONS = [
  { flag: '🇺🇸', name: 'United States', detail: 'Self-employment tax (15.3%), federal income tax, IRS Schedule C categories and quarterly 1040-ES estimates. State income tax guides for major states.', calc: '/tools/1099-tax-calculator', source: 'IRS', sourceUrl: 'https://www.irs.gov/businesses/small-businesses-self-employed' },
  { flag: '🇬🇧', name: 'United Kingdom', detail: 'Self Assessment income tax bands and Class 4 National Insurance for the self-employed and sole traders.', calc: '/tools/uk-self-assessment-tax-calculator', source: 'HMRC', sourceUrl: 'https://www.gov.uk/self-assessment-tax-returns' },
  { flag: '🇨🇦', name: 'Canada', detail: 'Business income on the T2125, federal and provincial income tax, and CPP contributions.', calc: '/tools/canada-self-employed-tax-calculator', source: 'CRA', sourceUrl: 'https://www.canada.ca/en/revenue-agency.html' },
  { flag: '🇩🇪', name: 'Germany', detail: 'Einkommensteuer (income tax), the Kleinunternehmer VAT rule and 19% Umsatzsteuer.', calc: '/tools/germany-self-employed-tax-calculator', source: 'Bundeszentralamt für Steuern', sourceUrl: 'https://www.bzst.de/' },
  { flag: '🇦🇪', name: 'United Arab Emirates', detail: '5% VAT and the AED 375,000 FTA registration threshold, with TRN capture on receipts.', calc: '/tools/uae-vat-calculator', source: 'UAE FTA', sourceUrl: 'https://tax.gov.ae/' },
  { flag: '🇸🇦', name: 'Saudi Arabia', detail: '15% VAT, ZATCA registration thresholds and e-invoicing (Fatoora) context.', calc: '/tools/saudi-arabia-vat-calculator', source: 'ZATCA', sourceUrl: 'https://zatca.gov.sa/' },
];

const STEPS = [
  { n: '1', title: 'Categorize as you go', desc: 'Each expense you text or scan is mapped to the correct local tax line automatically.' },
  { n: '2', title: 'Estimate what you owe', desc: 'Use the free per-country calculators to see your estimated tax and what to set aside.' },
  { n: '3', title: 'Export tax-ready reports', desc: 'Download a categorized summary (Schedule C, Self Assessment, T2125 or VAT) as PDF, CSV or Excel.' },
  { n: '4', title: 'Hand off to your accountant', desc: 'Give your bookkeeper or CPA clean, categorized data with receipts attached.' },
];

const FAQS = [
  { q: 'What tax jurisdictions does MoneyFeed support?', a: 'MoneyFeed supports US self-employment tax and Schedule C, UK Self Assessment, Canadian T2125, German income tax and VAT, and VAT for the UAE (5%) and Saudi Arabia (15% / ZATCA), plus multi-currency tracking and calculators for several other countries.' },
  { q: 'How does MoneyFeed calculate my taxes?', a: 'MoneyFeed categorizes your income and expenses to the right tax lines for your country and applies the current rates and thresholds to estimate what you owe. Estimates are for planning; your final figures should be confirmed when you file.' },
  { q: 'Are the tax rates kept up to date?', a: 'Rates, thresholds and deadlines are reviewed against official government sources (such as the IRS, HMRC, CRA, the UAE FTA and ZATCA) and updated for each tax year. Each country page shows when it was last reviewed.' },
  { q: 'Can I hand my MoneyFeed data to my accountant?', a: 'Yes. You can export categorized, accountant-ready reports as CSV, Excel or PDF, with receipt images attached, so your accountant or CPA can file from clean data.' },
  { q: 'Is this tax advice?', a: 'No. MoneyFeed keeps your books organized and provides estimates and tax-ready reports, but it does not replace advice from a qualified accountant or tax professional.' },
];

export default function TaxSupport() {
  const jsonLd = [
    { '@context': 'https://schema.org', '@type': 'WebPage', name: 'MoneyFeed Tax Support', url: 'https://moneyfeed.io/tax-support', description: 'The tax jurisdictions, calculations, categories and reports MoneyFeed supports — US Schedule C, UK Self Assessment, Canada T2125, Germany and GCC VAT, with government sources.' },
    { '@context': 'https://schema.org', '@type': 'FAQPage', mainEntity: FAQS.map((f) => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })) },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F8FAFC] via-[#ECFDF5] to-[#D1FAE5]" data-testid="tax-support-page">
      <Seo
        title="Tax Support — Jurisdictions, Calculations & Reports | MoneyFeed"
        description="Which taxes MoneyFeed supports and how: US Schedule C & 1040-ES, UK Self Assessment, Canada T2125, Germany, and UAE & Saudi VAT — with official government sources."
        path="/tax-support"
        jsonLd={jsonLd}
      />
      <LandingHeader />

      <nav className="max-w-6xl mx-auto px-4 sm:px-8 pt-6 text-xs text-gray-500 flex items-center gap-1" aria-label="Breadcrumb" data-testid="tax-support-breadcrumb">
        <Link to="/" className="hover:text-[#064E3B]">Home</Link>
        <ChevronRight className="h-3 w-3" /> <span className="text-[#064E3B] font-semibold">Tax Support</span>
      </nav>

      <section className="max-w-3xl mx-auto px-4 sm:px-8 pt-8 pb-6 text-center">
        <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-[#84CC16]/15 rounded-full mb-5">
          <Landmark className="h-4 w-4 text-[#65A30D]" />
          <span className="text-xs font-bold text-[#65A30D] uppercase tracking-wider">Tax Support</span>
        </div>
        <h1 className="text-4xl sm:text-5xl font-bold text-[#064E3B] leading-tight mb-5">Tax support, jurisdiction by jurisdiction</h1>
        <p className="text-base sm:text-lg text-gray-700 max-w-2xl mx-auto">
          MoneyFeed maps your income and expenses to the right tax lines, estimates what you owe, and exports tax-ready reports. Here’s exactly which taxes it supports and where the numbers come from.
        </p>
      </section>

      <section className="max-w-5xl mx-auto px-4 sm:px-8 py-10">
        <h2 className="text-2xl font-bold text-[#064E3B] mb-6">Supported jurisdictions</h2>
        <div className="space-y-4">
          {JURISDICTIONS.map((j) => (
            <div key={j.name} className="bg-white rounded-2xl shadow-md p-6" data-testid={`tax-jurisdiction-${j.name}`}>
              <div className="flex flex-col sm:flex-row sm:items-start gap-3">
                <span className="text-3xl" aria-hidden="true">{j.flag}</span>
                <div className="flex-1">
                  <h3 className="font-bold text-[#064E3B] mb-1">{j.name}</h3>
                  <p className="text-sm text-gray-600 mb-3">{j.detail}</p>
                  <div className="flex flex-wrap items-center gap-4">
                    <Link to={j.calc} className="inline-flex items-center gap-1 text-sm font-semibold text-[#065F46] hover:underline">
                      Tax calculator <ArrowRight className="h-3.5 w-3.5" />
                    </Link>
                    <a href={j.sourceUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-[#064E3B]">
                      Source: {j.source} <ExternalLink className="h-3 w-3" />
                    </a>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
        <p className="text-xs text-gray-400 mt-6">
          Rates, thresholds and deadlines are reviewed against the official government sources linked above. Last reviewed June 2026. MoneyFeed provides estimates and tax-ready records and does not replace professional tax advice.
        </p>
      </section>

      <section className="max-w-5xl mx-auto px-4 sm:px-8 py-10">
        <h2 className="text-2xl font-bold text-[#064E3B] mb-8 text-center">How tax works in MoneyFeed</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {STEPS.map((s) => (
            <div key={s.n} className="bg-white rounded-2xl shadow-md p-6" data-testid={`tax-step-${s.n}`}>
              <div className="w-10 h-10 rounded-full bg-[#064E3B] text-white font-bold flex items-center justify-center mb-4">{s.n}</div>
              <h3 className="font-bold text-[#064E3B] mb-1 text-sm">{s.title}</h3>
              <p className="text-gray-600 text-sm">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-4 sm:px-8 py-12">
        <h2 className="text-3xl font-bold text-[#064E3B] text-center mb-10">Tax support FAQ</h2>
        <div className="space-y-4">
          {FAQS.map((f, i) => (
            <div key={i} className="bg-white rounded-2xl shadow-sm p-6" data-testid={`tax-support-faq-${i}`}>
              <h3 className="font-bold text-[#064E3B] mb-2">{f.q}</h3>
              <p className="text-gray-600 text-sm">{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-4 sm:px-8 py-8">
        <div className="bg-[#064E3B] rounded-3xl p-10 text-center">
          <h2 className="text-3xl font-bold text-white mb-3">Stop dreading tax season</h2>
          <p className="text-[#A7F3D0] mb-6 max-w-xl mx-auto">Keep your books tax-ready all year — start free on WhatsApp.</p>
          <Link to="/auth">
            <Button className="bg-[#84CC16] hover:bg-[#65A30D] text-black font-bold rounded-full px-8 py-6 text-base" data-testid="tax-support-cta-signup">
              Get started free <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </Link>
        </div>
      </section>

      <RelatedLinks />

      <footer className="max-w-6xl mx-auto px-4 sm:px-8 py-8 text-center">
        <div className="flex flex-wrap justify-center gap-6 text-sm text-gray-500">
          <Link to="/" className="hover:text-[#064E3B]">Home</Link>
          <Link to="/supported-countries" className="hover:text-[#064E3B]">Supported Countries</Link>
          <Link to="/tools/1099-tax-calculator" className="hover:text-[#064E3B]">Tax Calculator</Link>
          <Link to="/for-accountants" className="hover:text-[#064E3B]">For Accountants</Link>
          <Link to="/blog" className="hover:text-[#064E3B]">Blog</Link>
        </div>
      </footer>
    </div>
  );
}
